import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/PhpAuthContext';
import { useData } from '../contexts/DataContext';
import { Button } from '../components/ui/button';
import { toast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';
import { 
  LogOut,
  Stethoscope,
  BarChart3,
  FileText,
  Users,
  Calendar,
  Settings,
  ClipboardList,
  Clock,
  DollarSign
} from 'lucide-react';

import DoctorStatsCards from '../components/doctor/DoctorStatsCards';
import PatientSubmissionsList from '../components/doctor/PatientSubmissionsList';
import SubmissionDetailsAndFeedback from '../components/doctor/SubmissionDetailsAndFeedback';
import DoctorQuickActions from '../components/doctor/DoctorQuickActions';
import DoctorRecentActivity from '../components/doctor/DoctorRecentActivity';
import AdminOverviewTab from '../components/admin/AdminOverviewTab';
import AdminPatientsTab from '../components/admin/AdminPatientsTab';
import AdminDoctorsTab from '../components/admin/AdminDoctorsTab';
import AppointmentsTab from '../components/admin/AppointmentsTab';
import AdminSubmissionsTab from '../components/admin/AdminSubmissionsTab';
import AdminSettingsTab from '../components/admin/AdminSettingsTab';
import DoctorSettingsTab from '../components/doctor/DoctorSettingsTab';
import CampSubmissions from '../components/doctor/CampSubmissions';
import Attendance from '../components/doctor/Attendance';
import RevenueReports from '../components/admin/RevenueReports';

const DoctorDashboard = () => {
  const { profile, signOut } = useAuth();
  const { submissions, patients, doctors, updateSubmission, fetchSubmissions, fetchPatients, fetchDoctors, addDoctor, updateDoctor } = useData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [nextAppointment, setNextAppointment] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [scheduledAppointmentsCount, setScheduledAppointmentsCount] = useState(0);
  const [campSubmissionsCount, setCampSubmissionsCount] = useState(0);
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [leaveRequestsCount, setLeaveRequestsCount] = useState(0);
  const [leaveRequestsPendingCount, setLeaveRequestsPendingCount] = useState(0);
  const [leaveRequestsApprovedCount, setLeaveRequestsApprovedCount] = useState(0);
  const [campSubmissionsInitialTab, setCampSubmissionsInitialTab] = useState('form');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [attendanceInitialTab, setAttendanceInitialTab] = useState('checkin');
  const [attendanceKey, setAttendanceKey] = useState(0);
  const [viewAsAdmin, setViewAsAdmin] = useState(false);

  // Refresh submissions after successful submission
  const refreshSubmissions = useCallback(() => {
    fetchSubmissions();
    fetchSubmissionsCount();
  }, [fetchSubmissions]);

  useEffect(() => {
    console.log('DoctorDashboard useEffect: Profile:', profile);
    if (profile) {
      console.log('DoctorDashboard: Profile role:', profile.role);
      fetchSubmissions();
      if (profile.role === 'admin' || profile.role === 'doctor' || profile.role === 'chief_doctor' || profile.role === 'clinic_login') {
        console.log('DoctorDashboard: Fetching data for role:', profile.role);
        // When viewAsAdmin is true, fetch all patients (no filter)
        // When viewAsAdmin is false, fetchPatients() will be called by AdminPatientsTab with proper filter
        if (viewAsAdmin && profile.role === 'chief_doctor') {
          // Fetch all patients when viewing as admin (pass viewAsAdmin flag)
          fetchPatients(null, true);
          fetchDoctors();
        } else {
          // Regular fetch - AdminPatientsTab will handle filtering
          fetchPatients();
        }
        fetchAppointmentsCount();
        fetchAppointments();
        fetchSubmissionsCount();
        fetchAttendanceCount();
        if (viewAsAdmin && profile.role === 'chief_doctor') {
          fetchLeaveRequestsCount();
        }
      } else {
        console.warn('DoctorDashboard: Role not allowed:', profile.role);
      }
    } else {
      console.log('DoctorDashboard: No profile available');
    }
  }, [profile, fetchSubmissions, fetchPatients, fetchDoctors, viewAsAdmin]);

  // Update current date/time every second (IST)
  useEffect(() => {
    const updateDateTime = () => {
      setCurrentDateTime(new Date());
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchCampSubmissionsCount = useCallback(async (overrideViewAsAdmin = null) => {
    try {
      // Use overrideViewAsAdmin if provided, otherwise use current viewAsAdmin state
      const shouldViewAsAdmin = overrideViewAsAdmin !== null ? overrideViewAsAdmin : viewAsAdmin;
      const currentProfile = profile;
      
      // If viewAsAdmin is true for chief_doctor, fetch all submissions like admin
      const url = shouldViewAsAdmin && currentProfile?.role === 'chief_doctor' 
        ? 'get_camp_submissions.php?limit=1&offset=0&view_as_admin=true'
        : 'get_camp_submissions.php?limit=1&offset=0';
      
      console.log('Fetching camp submissions count:', {
        url,
        shouldViewAsAdmin,
        overrideViewAsAdmin,
        currentViewAsAdmin: viewAsAdmin,
        role: currentProfile?.role
      });
      
      const data = await apiRequest(url);
      
      console.log('Camp submissions count response:', data);
      
      if (data && data.success !== false) {
        const count = data.total || 0;
        console.log('Setting camp submissions count to:', count);
        setCampSubmissionsCount(count);
      } else {
        console.error('Failed to fetch camp submissions count:', data?.error || 'Unknown error');
        setCampSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching camp submissions count:', error);
      setCampSubmissionsCount(0);
    }
  }, [viewAsAdmin, profile]);

  const fetchAttendanceCount = useCallback(async (overrideViewAsAdmin = null) => {
    try {
      const shouldViewAsAdmin = overrideViewAsAdmin !== null ? overrideViewAsAdmin : viewAsAdmin;
      const url = shouldViewAsAdmin && profile?.role === 'chief_doctor'
        ? 'get_attendance.php?limit=1&offset=0&view_as_admin=true'
        : 'get_attendance.php?limit=1&offset=0';
      const data = await apiRequest(url);
      if (data.success) {
        setAttendanceCount(data.total || 0);
      } else {
        console.error('Failed to fetch attendance count:', data.error);
        setAttendanceCount(0);
      }
    } catch (error) {
      console.error('Error fetching attendance count:', error);
      setAttendanceCount(0);
    }
  }, [viewAsAdmin, profile]);

  const fetchLeaveRequestsCount = useCallback(async () => {
    try {
      const data = await apiRequest('get_leave_requests.php?limit=1000&offset=0&view_as_admin=true');
      if (data.success) {
        const leaveRequests = data.leave_requests || [];
        setLeaveRequestsCount(leaveRequests.length);
        // Case-insensitive status comparison
        const pending = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'pending').length;
        const approved = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'approved').length;
        setLeaveRequestsPendingCount(pending);
        setLeaveRequestsApprovedCount(approved);
      } else {
        console.error('Failed to fetch leave requests count:', data.error);
        setLeaveRequestsCount(0);
        setLeaveRequestsPendingCount(0);
        setLeaveRequestsApprovedCount(0);
      }
    } catch (error) {
      console.error('Error fetching leave requests count:', error);
      setLeaveRequestsCount(0);
      setLeaveRequestsPendingCount(0);
      setLeaveRequestsApprovedCount(0);
    }
  }, []);

  // Fetch camp submissions count when viewAsAdmin or profile changes
  useEffect(() => {
    if (profile && (profile.role === 'admin' || profile.role === 'doctor' || profile.role === 'chief_doctor' || profile.role === 'clinic_login')) {
      console.log('useEffect triggered - fetching camp submissions count, viewAsAdmin:', viewAsAdmin, 'role:', profile.role);
      fetchCampSubmissionsCount();
      fetchAttendanceCount();
      if (viewAsAdmin && profile.role === 'chief_doctor') {
        fetchLeaveRequestsCount();
      }
    }
  }, [profile, viewAsAdmin, fetchCampSubmissionsCount, fetchAttendanceCount]);

  const fetchAppointmentsCount = async () => {
    try {
      const data = await apiRequest('get_appointments_count.php');
      if (data.success) {
        setAppointmentsCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching appointments count:', error);
      setAppointmentsCount(0);
    }
  };

  const fetchAppointments = async () => {
    try {
      // If viewAsAdmin is true for chief_doctor, fetch all appointments like admin
      // Otherwise, filter by doctor_id if role is doctor and can't view other patients
      let url = 'get_appointments_list.php';
      if ((!viewAsAdmin && profile?.role === 'doctor' && !profile?.can_view_other_patients) || (!viewAsAdmin && profile?.role === 'clinic_login')) {
        url = `get_appointments_list.php?doctor_id=${profile.id}`;
      } else if (!viewAsAdmin && profile?.role === 'chief_doctor') {
        // Chief doctor viewing as doctor - only show their appointments
        url = `get_appointments_list.php?doctor_id=${profile.id}`;
      }
      const data = await apiRequest(url);
      if (data.success) {
        const appointmentsList = data.appointments || [];
        setAppointments(appointmentsList);
        // Count scheduled appointments
        const scheduled = appointmentsList.filter(apt => 
          (apt.status || '').toLowerCase() === 'scheduled'
        ).length;
        setScheduledAppointmentsCount(scheduled);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
      setScheduledAppointmentsCount(0);
    }
  };

  const fetchSubmissionsCount = async () => {
    try {
      const data = await apiRequest('get_submissions_count.php');
      if (data.success) {
        setSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch submissions count:', data.error);
        setSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching submissions count:', error);
      setSubmissionsCount(0);
    }
  };

  // Filter submissions based on viewAsAdmin mode
  const doctorSubmissions = React.useMemo(() => {
    if (viewAsAdmin || profile?.role === 'admin') {
      // Show all submissions when viewing as admin
      return submissions;
    } else if (profile?.role === 'chief_doctor') {
      // Chief doctor viewing as doctor - only show their submissions
      return submissions.filter(sub => sub.doctor_id === profile.id);
    } else {
      // Regular doctor - only their submissions
      return submissions.filter(sub => sub.doctor_id === profile?.id);
    }
  }, [submissions, viewAsAdmin, profile]);

  // When viewAsAdmin is true, use exact same tabs as AdminDashboard
  // When viewAsAdmin is false, use doctor tabs (conditionally show camp_submissions and attendance based on permissions)
  const baseTabs = viewAsAdmin && profile?.role === 'chief_doctor' 
    ?       [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'doctors', label: 'Doctors', icon: Users },
        { id: 'patients', label: 'Patients', icon: Stethoscope },
        { id: 'appointments', label: 'Appointments', icon: Calendar },
        { id: 'submissions', label: 'Appointment History', icon: FileText },
        { id: 'revenue', label: 'Revenue Reports', icon: DollarSign },
        { id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList },
        { id: 'attendance', label: 'Attendance', icon: Clock },
        { id: 'settings', label: 'Settings', icon: Settings }
      ]
    : [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'patients', label: 'Patients', icon: Users },
        { id: 'appointments', label: 'Appointments', icon: Calendar },
        { id: 'submissions', label: 'Appointment History', icon: FileText },
        { id: 'revenue', label: 'Revenue Reports', icon: DollarSign },
        // Conditionally add camp_submissions and attendance based on permissions
        ...(profile?.can_access_camp_submissions ? [{ id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList }] : []),
        ...(profile?.can_access_attendance ? [{ id: 'attendance', label: 'Attendance', icon: Clock }] : []),
        { id: 'settings', label: 'Settings', icon: Settings }
      ];
  
  // Use baseTabs as tabs (for compatibility with existing code)
  const tabs = baseTabs;

  const handleTabChange = (tabId, subTab = null) => {
    // Handle attendance tab navigation first (before setting activeTab)
    if (tabId === 'attendance') {
      if (subTab) {
        // Set the specific sub-tab (e.g., 'approve_leave')
        setAttendanceInitialTab(subTab);
        // Force remount by updating key if already on attendance tab
        if (activeTab === 'attendance') {
          setAttendanceKey(prev => prev + 1);
        }
      } else if (activeTab === 'overview') {
        // Default to checkin when navigating from overview without subTab
        setAttendanceInitialTab('checkin');
      }
    } else if (tabId !== 'attendance') {
      // Reset to 'checkin' when navigating away from attendance
      setAttendanceInitialTab('checkin');
    }
    
    // If navigating to camp_submissions from overview (via count card), set initial tab to 'list'
    if (tabId === 'camp_submissions' && activeTab === 'overview') {
      setCampSubmissionsInitialTab('list');
    } else if (tabId !== 'camp_submissions') {
      // Reset to 'form' when navigating away
      setCampSubmissionsInitialTab('form');
    }
    setActiveTab(tabId);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    toast({
      title: 'Logged out successfully',
      description: 'See you next time!'
    });
  };

  const handleSubmissionClick = (submission) => {
    setSelectedSubmission(submission);
    setFeedback(submission.doctor_feedback || '');
    setRecommendations(submission.recommendations || '');
    setNextAppointment(submission.next_appointment || '');
    setSubmissionStatus(submission.status || 'pending');
  };

  const handleUpdateSubmission = async () => {
    if (!selectedSubmission) return;

    const updates = {
      doctor_feedback: feedback,
      recommendations: recommendations,
      next_appointment: nextAppointment,
      status: submissionStatus,
      reviewed_at: new Date().toISOString(),
    };

    const { error } = await updateSubmission(selectedSubmission.id, updates);
    
    if (!error) {
      toast({
        title: 'Submission updated successfully',
        description: 'Patient has been notified of your feedback'
      });
      setSelectedSubmission(null);
    } else {
      toast({
        title: 'Error updating submission',
        variant: 'destructive'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Doctor Dashboard - Gugan Dental Care</title>
        <meta name="description" content="Manage patient submissions, review dental health forms, and provide medical feedback through your doctor dashboard." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <img 
                  src="/logo.png" 
                  alt="Gugan Dental Care Logo" 
                  className="h-8 sm:h-10 md:h-12 w-auto object-contain"
                  onError={(e) => {
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      e.target.style.display = 'none';
                      fallback.style.display = 'block';
                    }
                  }}
                />
                <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 hidden" />
                <span className="text-base sm:text-xl font-bold gradient-text">Gugan Dental Care</span>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* View as Admin checkbox - only for chief_doctor */}
                {profile?.role === 'chief_doctor' && (
                  <label className="flex items-center space-x-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={viewAsAdmin}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setViewAsAdmin(newValue);
                        // Reset to overview tab when toggling
                        handleTabChange('overview');
                        // Refetch data when toggling
                        if (newValue && profile?.role === 'chief_doctor') {
                          fetchDoctors();
                          // Fetch all patients when viewing as admin
                          fetchPatients(null, true);
                        } else {
                          // Fetch patients normally when not viewing as admin
                          fetchPatients();
                        }
                        fetchAppointments();
                        fetchSubmissions();
                        // Explicitly fetch camp submissions count with new viewAsAdmin value
                        fetchCampSubmissionsCount(newValue);
                        fetchAttendanceCount(newValue);
                        if (newValue && profile?.role === 'chief_doctor') {
                          fetchLeaveRequestsCount();
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 cursor-pointer"
                    />
                    <span className="text-white text-xs sm:text-sm group-hover:text-blue-400 transition-colors">
                      View as Admin
                    </span>
                  </label>
                )}
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-white text-xs sm:text-sm">Welcome, Dr. {profile?.full_name || profile?.name || 'Doctor'}</span>
                  {profile?.doctor_unique_id && (
                    <span className="text-gray-400 text-xs mt-0.5">Doctor Unique ID: {profile.doctor_unique_id}</span>
                  )}
                </div>
                <span className="sm:hidden text-white text-xs truncate max-w-[120px]">Dr. {profile?.full_name || profile?.name || 'Doctor'}</span>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-red-400 p-2 sm:px-3 sm:py-2"
                  size="sm"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1 sm:mb-2">
                  {viewAsAdmin && profile?.role === 'chief_doctor' ? 'Admin Dashboard' : 'Doctor Dashboard'}
                </h1>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">
                  {viewAsAdmin && profile?.role === 'chief_doctor' 
                    ? 'Manage your dental practice and system settings'
                    : 'Manage patients, review submissions, and provide medical feedback'}
                </p>
              </div>
              <div className="text-right">
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Current Date & Time (IST)</p>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    {currentDateTime.toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                  <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                    {currentDateTime.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {activeTab === 'overview' && (
              viewAsAdmin && profile?.role === 'chief_doctor' ? (
                // Admin Overview Tab when viewing as admin
                  <AdminOverviewTab 
                  stats={{
                    total: submissionsCount || submissions.length,
                    inProgress: submissions.filter(s => s.status === 'in-progress').length,
                    completed: submissions.filter(s => s.status === 'completed').length,
                    activeDoctors: doctors.filter(d => d.status === 'Active').length,
                    activeDoctorsDisplay: `${doctors.filter(d => d.status === 'Active').length}(${doctors.length})`,
                    totalDoctors: doctors.length,
                    totalPatients: patients.length,
                    totalAppointments: appointmentsCount,
                    campSubmissions: campSubmissionsCount
                  }}
                  submissions={submissions}
                  patients={patients}
                  appointments={appointments}
                  doctors={doctors}
                  appointmentsCount={appointmentsCount}
                  campSubmissionsCount={campSubmissionsCount}
                  attendanceCount={attendanceCount}
                  leaveRequestsCount={leaveRequestsCount}
                  leaveRequestsPendingCount={leaveRequestsPendingCount}
                  leaveRequestsApprovedCount={leaveRequestsApprovedCount}
                  onNavigate={handleTabChange}
                />
              ) : (
                // Doctor Overview Tab when viewing as doctor
                <>
                  {/* Stats Cards */}
                  <DoctorStatsCards 
                    doctorSubmissions={doctorSubmissions} 
                    patients={patients} 
                    appointmentsCount={appointmentsCount}
                    scheduledAppointmentsCount={scheduledAppointmentsCount}
                    submissionsCount={submissionsCount}
                    campSubmissionsCount={campSubmissionsCount}
                    attendanceCount={attendanceCount}
                    onNavigate={handleTabChange}
                    canAccessCampSubmissions={profile?.can_access_camp_submissions || false}
                    canAccessAttendance={profile?.can_access_attendance || false}
                  />

                  <div className="flex flex-col lg:flex-row gap-8 mt-8">
                    {/* Patient Submissions - 65% width */}
                    <div className="w-full lg:w-[65%]">
                      <PatientSubmissionsList 
                        doctorSubmissions={doctorSubmissions} 
                        handleSubmissionClick={handleSubmissionClick} 
                        selectedSubmission={selectedSubmission}
                        onRefresh={refreshSubmissions}
                      />
                    </div>

                    {/* Submission Details & Feedback / Doctor Profile - 35% width */}
                    <div className="w-full lg:w-[35%]">
                      <SubmissionDetailsAndFeedback
                        selectedSubmission={selectedSubmission}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        recommendations={recommendations}
                        setRecommendations={setRecommendations}
                        nextAppointment={nextAppointment}
                        setNextAppointment={setNextAppointment}
                        submissionStatus={submissionStatus}
                        setSubmissionStatus={setSubmissionStatus}
                        handleUpdateSubmission={handleUpdateSubmission}
                        setSelectedSubmission={setSelectedSubmission}
                        profile={profile}
                      />
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <DoctorRecentActivity 
                    submissions={doctorSubmissions}
                    appointments={appointments}
                    profile={profile}
                  />

                  {/* Quick Actions - Hidden */}
                  {/* <DoctorQuickActions /> */}
                </>
              )
            )}
            {activeTab === 'submissions' && (
              viewAsAdmin && profile?.role === 'chief_doctor' ? (
                <AdminSubmissionsTab submissions={submissions} />
              ) : (
                <PatientSubmissionsList 
                  doctorSubmissions={doctorSubmissions} 
                  handleSubmissionClick={handleSubmissionClick} 
                  selectedSubmission={selectedSubmission}
                  onRefresh={refreshSubmissions}
                />
              )
            )}
            {activeTab === 'doctors' && <AdminDoctorsTab doctors={doctors} addDoctor={addDoctor} updateDoctor={updateDoctor} />}
            {activeTab === 'patients' && <AdminPatientsTab onSubmissionComplete={refreshSubmissions} viewAsAdmin={viewAsAdmin} />}
            {activeTab === 'appointments' && <AppointmentsTab viewAsAdmin={viewAsAdmin} />}
            {activeTab === 'revenue' && <RevenueReports viewAsAdmin={viewAsAdmin && profile?.role === 'chief_doctor'} />}
            {activeTab === 'camp_submissions' && <CampSubmissions initialTab={campSubmissionsInitialTab} viewAsAdmin={viewAsAdmin && profile?.role === 'chief_doctor'} />}
            {activeTab === 'attendance' && <Attendance key={`attendance-${attendanceInitialTab}-${attendanceKey}`} initialTab={attendanceInitialTab} viewAsAdmin={viewAsAdmin && profile?.role === 'chief_doctor'} isAdmin={viewAsAdmin && profile?.role === 'chief_doctor'} />}
            {activeTab === 'settings' && (
              viewAsAdmin && profile?.role === 'chief_doctor' ? (
                <AdminSettingsTab />
              ) : (
                <DoctorSettingsTab />
              )
            )}
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo.png" 
                alt="Gugan Dental Care Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    e.target.style.display = 'none';
                    fallback.style.display = 'block';
                  }
                }}
              />
              <Stethoscope className="h-6 w-6 text-blue-400 hidden" />
              <span className="text-lg font-semibold gradient-text">Gugan Dental Care</span>
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 Gugan Dental Care. All rights reserved. Developed and maintained by{' '}
              <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DoctorDashboard;